import { useTranslation } from 'next-i18next';
import { useCallback, useEffect, useState } from 'react';

import { ButtonV3 } from 'components/ComponentV2';
import { useCandidateProfile } from 'lib/contexts/candidate-profile-context';
import { ExtraCurricularAttributes } from 'lib/models/candidate';
import { useAuth } from 'lib/providers/AuthProvider';

import { formatDate } from 'utils/format';
import EmptyBottomSection from '../EmptyBottomSection';
import FilledBottomSection from '../FilledBottomSection';
import TopTitleBar from '../TopTitleBar';

import styles from '../Profile.common.module.scss';
import { AddExtraCurricularModal } from './AddExtraCurricularModal.component';
import { getExtraCurricularInfo } from './utils';

export const ExtraCurricular = ({ className }: { className?: string }) => {
  const [isModalOpen, setModalOpen] = useState(false);
  const [selectedExperienceInfo, setSelectedExperienceInfo] = useState<
    (ExtraCurricularAttributes & { isNew: boolean }) | null
  >(null);

  const { extraCurricularComplete } = useCandidateProfile();
  const [t] = useTranslation('candidate.profile');

  const getExtraCurricularDetails = (item: ExtraCurricularAttributes) => {
    const startDateFormatted = item?.start_date
      ? formatDate(new Date(item.start_date))
      : '';
    const endDateFormatted = item?.presently_engaged
      ? t('extra-curricular.ongoing')
      : item?.end_date
      ? formatDate(new Date(item.end_date))
      : '';

    return {
      title: `${item?.role}, ${item.organisation_name}`,
      startDate: startDateFormatted,
      endDate: endDateFormatted,
      description: item?.description || '',
      id: item?.id || '',
    };
  };

  const { candidate } = useAuth();
  const [extraCurricularInfo, setExtraCurricularInfo] = useState<
    Array<ExtraCurricularAttributes & { isNew: boolean }>
  >([]);

  useEffect(() => {
    if (candidate) {
      setExtraCurricularInfo(getExtraCurricularInfo(candidate));
    }
  }, [candidate]);

  const dataForExtraCurricular = extraCurricularInfo.map(
    getExtraCurricularDetails
  );

  const addExtraCurricular = useCallback(() => {
    setSelectedExperienceInfo(null);
    setModalOpen(true);
  }, []);

  const onEditClick = (id: string) => {
    const info = extraCurricularInfo.find((item) => item.id === id);
    if (info) {
      setSelectedExperienceInfo(info);
      setModalOpen(true);
    }
  };

  return (
    <article className={className}>
      <TopTitleBar
        title={t('extra-curricular.title')}
        isInfoComplete={extraCurricularComplete}
        iconName="icon_plus"
        onIconClick={addExtraCurricular}
      />
      {extraCurricularComplete ? (
        <section className={styles.content}>
          {dataForExtraCurricular.map((item, index) => (
            <FilledBottomSection
              key={index}
              id={item.id}
              title={item.title}
              startDate={item.startDate}
              endDate={item.endDate}
              isLast={dataForExtraCurricular.length - 1 === index}
              description={item.description}
              onEditClick={() => onEditClick(item.id)}
            />
          ))}
          <ButtonV3
            onClick={addExtraCurricular}
            startIcon="icon_plus"
            size="small"
            color="secondary"
          >
            {t('extra-curricular.button-text')}
          </ButtonV3>
        </section>
      ) : (
        <EmptyBottomSection
          desc={t('extra-curricular.desc')}
          addButtonText={t('extra-curricular.button-text')}
          createWithAIButton={true}
          onAddIconClick={addExtraCurricular}
          onAIButtonClick={addExtraCurricular}
        />
      )}
      {isModalOpen ? (
        <AddExtraCurricularModal
          isOpen={isModalOpen}
          onClose={() => {
            setModalOpen(false);
            setSelectedExperienceInfo(null);
          }}
          isEdit={selectedExperienceInfo === null ? false : true}
          selectedExtraCurricularInfo={selectedExperienceInfo}
        />
      ) : null}
    </article>
  );
};
